import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, ShieldCheck, Sparkles, Swords, TimerReset } from 'lucide-react';
import BrandMark from '../components/BrandMark';

const backgroundTerms = [
  { label: 'TDAH', size: 'text-3xl md:text-6xl', top: '10%', left: '7%', speed: 0.14, drift: -22, delay: '0s', duration: '11s' },
  { label: 'TEA', size: 'text-2xl md:text-5xl', top: '18%', left: '78%', speed: 0.16, drift: -18, delay: '0.6s', duration: '10s' },
  { label: 'DOPAMINA', size: 'text-4xl md:text-7xl', top: '15%', left: '44%', speed: 0.18, drift: 28, delay: '1.4s', duration: '13s' },
  { label: 'FOCO', size: 'text-5xl md:text-8xl', top: '28%', left: '9%', speed: 0.22, drift: -26, delay: '0.8s', duration: '10s' },
  { label: 'HIPERFOCO', size: 'text-2xl md:text-5xl', top: '38%', left: '55%', speed: 0.16, drift: 24, delay: '2s', duration: '12s' },
  { label: 'PROCRASTINACAO', size: 'text-xl md:text-4xl', top: '50%', left: '8%', speed: 0.2, drift: 18, delay: '1s', duration: '14s' },
  { label: 'AH/SD', size: 'text-3xl md:text-6xl', top: '60%', left: '61%', speed: 0.24, drift: -18, delay: '2.6s', duration: '11s' },
  { label: 'PREGUICA', size: 'text-xl md:text-4xl', top: '70%', left: '18%', speed: 0.16, drift: 20, delay: '1.8s', duration: '13s' },
  { label: 'ANSIEDADE', size: 'text-xl md:text-4xl', top: '77%', left: '58%', speed: 0.14, drift: -16, delay: '0.4s', duration: '12s' },
  { label: 'EXECUCAO', size: 'text-2xl md:text-5xl', top: '89%', left: '14%', speed: 0.19, drift: 12, delay: '2.2s', duration: '11s' },
  { label: 'CONSISTENCIA', size: 'text-2xl md:text-5xl', top: '96%', left: '46%', speed: 0.17, drift: -20, delay: '1.2s', duration: '13s' },
  { label: 'FOCO', size: 'text-2xl md:text-4xl', top: '22%', left: '68%', speed: 0.15, drift: -12, delay: '3s', duration: '9s' },
  { label: 'TDAH', size: 'text-xl md:text-4xl', top: '44%', left: '28%', speed: 0.13, drift: 14, delay: '2.1s', duration: '10s' },
  { label: 'DOPAMINA', size: 'text-2xl md:text-5xl', top: '82%', left: '70%', speed: 0.18, drift: 16, delay: '0.9s', duration: '12s' },
  { label: 'HIPERFOCO', size: 'text-lg md:text-4xl', top: '8%', left: '72%', speed: 0.12, drift: -10, delay: '1.6s', duration: '11s' },
  { label: 'ROTINA', size: 'text-xl md:text-4xl', top: '34%', left: '38%', speed: 0.14, drift: 12, delay: '2.8s', duration: '13s' },
  { label: 'TRAVAS', size: 'text-xl md:text-4xl', top: '58%', left: '36%', speed: 0.17, drift: -14, delay: '1.1s', duration: '10s' },
  { label: 'IMPULSO', size: 'text-xl md:text-4xl', top: '66%', left: '74%', speed: 0.16, drift: 18, delay: '2.4s', duration: '12s' },
  { label: 'DESORGANIZACAO', size: 'text-sm md:text-3xl', top: '92%', left: '62%', speed: 0.11, drift: -12, delay: '3.2s', duration: '14s' },
  { label: 'DISCIPLINA', size: 'text-lg md:text-4xl', top: '26%', left: '24%', speed: 0.15, drift: 10, delay: '1.7s', duration: '12s' },
  { label: 'RECOMECO', size: 'text-lg md:text-4xl', top: '74%', left: '8%', speed: 0.14, drift: 14, delay: '2.9s', duration: '13s' },
];

const pillars = [
  {
    icon: Brain,
    title: 'Feito para mentes intensas',
    description: 'O app traduz caos mental em pequenas ações, progresso visível e menos culpa na hora de começar.'
  },
  {
    icon: TimerReset,
    title: 'Menos fricção, mais ação',
    description: 'Tudo foi desenhado para reduzir atrito: registrar, concluir, evoluir e voltar para o que importa.'
  },
  {
    icon: Swords,
    title: 'Gamificação que puxa para frente',
    description: 'XP, desafios, ranking, teia de atributos e Modo Foco para transformar consistência em jogo real.'
  },
];

const audienceCards = [
  {
    title: 'Para neurodivergentes',
    text: 'Ajuda quem lida com TDAH, hiperfoco, oscilação de energia, procrastinação e dificuldade de manter ritmo.'
  },
  {
    title: 'Para neurotípicos',
    text: 'Também funciona para quem quer estrutura, clareza, metas curtas e um sistema mais interessante que uma to-do list comum.'
  }
];

const proofPoints = [
  'Transforma tarefas em progresso de personagem',
  'Une Base, desafios e Modo Foco no mesmo sistema',
  'Mostra evolução com feedback visual imediato',
  'Ajuda a sair do scroll e voltar para a execução',
  'Faz produtividade parecer jornada, não castigo'
];

const Landing = () => {
  const landingRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let frameId = 0;

    const syncScrollVariable = () => {
      frameId = 0;
      landingRef.current?.style.setProperty('--landing-scroll-y', `${window.scrollY}px`);
    };

    const handleScroll = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(syncScrollVariable);
    };

    syncScrollVariable();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, []);

  const handleScrollToHowItWorks = () => {
    // Troquei o href por scroll programático porque o HashRouter estava recarregando a página no mobile.
    const section = document.getElementById('como-funciona');
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div ref={landingRef} className="relative min-h-screen overflow-hidden bg-background text-white">
      <div className="pointer-events-none fixed inset-0 z-0 bg-[radial-gradient(circle_at_top,rgba(168,85,247,0.18),transparent_30%),radial-gradient(circle_at_78%_16%,rgba(91,33,182,0.16),transparent_24%),radial-gradient(circle_at_bottom,rgba(168,85,247,0.12),transparent_22%)]" />
      <div className="pointer-events-none fixed inset-0 z-0 bg-[linear-gradient(180deg,rgba(7,7,13,0.18),rgba(7,7,13,0.72)_44%,rgba(7,7,13,0.92))]" />

      <div className="landing-word-cloud" aria-hidden="true">
        {backgroundTerms.map((term) => (
          <span
            key={term.label}
            className="landing-word-shell"
            style={{
              top: term.top,
              left: term.left,
              ['--landing-speed' as string]: term.speed,
              ['--landing-drift' as string]: term.drift,
            }}
          >
            <span
              className={`landing-word ${term.size}`}
              style={{
                animationDelay: term.delay,
                animationDuration: term.duration,
              }}
            >
              {term.label}
            </span>
          </span>
        ))}
      </div>

      <header className="fixed inset-x-0 top-0 z-30 px-3 py-3 md:px-6 md:py-4">
        <div className="mx-auto flex w-full max-w-lg items-center gap-2 rounded-[30px] border border-gray-800 bg-card/92 px-3 py-4 shadow-[0_18px_40px_rgba(0,0,0,0.38)] backdrop-blur-xl md:max-w-4xl md:gap-3 md:px-6">
          <div className="min-w-0 flex-1">
            <BrandMark
              className="min-w-0"
              imageClassName="h-9 w-9 rounded-2xl"
              titleClassName="text-[13px] font-bold uppercase tracking-[0.18em] text-neon-purple sm:text-sm sm:tracking-[0.26em] md:tracking-[0.42em]"
              labelClassName="min-w-0"
            />
          </div>

          <div className="flex shrink-0 items-center gap-2 self-start md:self-center">
            <Link
              to="/login"
              className="inline-flex min-h-[58px] items-center justify-center rounded-full border border-gray-700 bg-[#15192a] px-3 text-sm font-bold whitespace-nowrap text-white transition hover:border-gray-500 hover:bg-[#1a2035] md:min-h-[70px] md:px-8 md:text-2xl"
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="inline-flex min-h-[58px] items-center justify-center rounded-full bg-neon-purple px-3 text-sm font-bold whitespace-nowrap text-white transition hover:bg-purple-600 md:min-h-[70px] md:px-8 md:text-2xl"
            >
              Criar conta
            </Link>
          </div>
        </div>
      </header>

      <main className="relative z-10">
        <section className="mx-auto max-w-lg px-4 pb-14 pt-36 md:max-w-4xl md:px-6 md:pb-14 md:pt-40">
          <div className="rounded-[32px] border border-gray-800 bg-card/70 p-5 shadow-[0_24px_70px_rgba(0,0,0,0.28)] backdrop-blur md:p-8">
            <div className="max-w-3xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-neon-purple/20 bg-neon-purple/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.25em] text-purple-200 md:text-sm">
                <Sparkles size={16} />
                Menos culpa. Mais execução com recompensa real.
              </div>

              <h1 className="max-w-3xl font-display text-4xl font-bold leading-[0.95] text-white md:text-7xl">
                O sistema para quem vive entre foco, travas, dopamina curta e dificuldade de entrar em movimento.
              </h1>

              <p className="mt-5 max-w-2xl text-base leading-8 text-slate-300 md:text-xl">
                O Dopamind pega rotina, tarefas, desafios e foco e transforma tudo em progresso visível. Foi pensado para quem
                se identifica com TDAH, procrastinação, hiperfoco, oscilação de energia e excesso de estímulo, sem deixar de
                servir quem só quer um sistema melhor para agir.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-neon-purple px-6 py-4 text-base font-bold text-white transition hover:bg-purple-600"
                >
                  Criar conta e começar
                  <ArrowRight size={18} />
                </Link>
                <button
                  type="button"
                  onClick={handleScrollToHowItWorks}
                  className="inline-flex items-center justify-center rounded-2xl border border-gray-700 bg-background/60 px-6 py-4 text-base font-bold text-white transition hover:bg-card"
                >
                  Ver como funciona
                </button>
              </div>

              <div className="mt-8 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
                {proofPoints.map((item) => (
                  <div key={item} className="rounded-2xl border border-gray-800 bg-background/70 px-4 py-4 backdrop-blur">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="como-funciona" className="mx-auto max-w-lg px-4 py-12 md:max-w-4xl md:px-6 md:py-10">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.35em] text-neon-purple/80">Como funciona</p>
            <h2 className="mt-4 font-display text-3xl font-bold text-white md:text-5xl">
              O app pega o que hoje parece peso mental e transforma em um sistema jogável.
            </h2>
          </div>

          <div className="mt-8 grid gap-4 md:mt-10 md:grid-cols-3">
            {pillars.map((pillar) => {
              const Icon = pillar.icon;

              return (
                <article key={pillar.title} className="rounded-[28px] border border-gray-800 bg-card/72 p-5 backdrop-blur md:p-6">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-neon-purple/10 text-neon-purple">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-5 text-2xl font-semibold text-white">{pillar.title}</h3>
                  <p className="mt-3 text-base leading-7 text-slate-300">{pillar.description}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="mx-auto max-w-lg px-4 py-12 md:max-w-4xl md:px-6 md:py-10">
          <div className="grid gap-4 md:grid-cols-2">
            {audienceCards.map((card) => (
              <article key={card.title} className="rounded-[28px] border border-gray-800 bg-card/72 p-6 backdrop-blur-xl md:p-8">
                <div className="inline-flex rounded-full border border-gray-700 bg-background/70 px-4 py-2 text-sm text-slate-300">
                  {card.title}
                </div>
                <p className="mt-5 text-xl leading-9 text-white md:text-2xl">{card.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-lg px-4 py-12 md:max-w-4xl md:px-6 md:py-10">
          <div className="rounded-[32px] border border-gray-800 bg-card/76 p-6 shadow-[0_18px_60px_rgba(0,0,0,0.28)] backdrop-blur-xl md:p-10">
            <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-end">
              <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 rounded-full border border-neon-purple/20 bg-neon-purple/10 px-4 py-2 text-sm text-purple-100">
                  <ShieldCheck size={16} />
                  Clareza para começar. Recompensa para continuar.
                </div>
                <h2 className="mt-5 font-display text-3xl font-bold text-white md:text-5xl">
                  Se identificar com isso já é um sinal de que esse sistema foi feito para você.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-8 text-slate-300 md:text-lg">
                  O Dopamind transforma rotina, tarefa, challenge e foco em progresso visível. Em vez de depender só de
                  motivação, você passa a ter estrutura, feedback rápido e uma sensação real de evolução a cada passo.
                </p>
                <p className="mt-6 text-xs uppercase tracking-[0.28em] text-gray-500">
                  Criado por
                  <br />
                  R² Solutions Group – Tech & Consulting
                  <br />
                  Mais do que Tech. Soluções com propósito
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center rounded-2xl bg-neon-purple px-6 py-4 text-base font-bold text-white transition hover:bg-purple-600"
                >
                  Criar conta agora
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center rounded-2xl border border-gray-700 bg-background/70 px-6 py-4 text-base font-bold text-white transition hover:bg-card"
                >
                  Já tenho login
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Landing;
